import { loadStripe, Stripe } from '@stripe/stripe-js';
import { FeatureFlag } from '@signup/shared';
import { getStripePublishableKey, isFeatureEnabled } from './configurations';

let stripePromise: Promise<Stripe | null> | null = null;

/** Clears cached Stripe.js loader between unit tests. */
export function resetStripeLoaderCacheForTests(): void {
  stripePromise = null;
}

function getStripeLoader(): Promise<Stripe | null> | null {
  if (!isFeatureEnabled(FeatureFlag.FF_ENABLE_STRIPE_RADAR_REGISTRATION)) {
    return null;
  }
  const publishableKey = getStripePublishableKey();
  if (!publishableKey) {
    return null;
  }
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}

/**
 * Create a Radar Session after client-side validation and before createtrial.
 * Fail-open: returns undefined if Stripe.js is unavailable or createRadarSession errors.
 */
export async function createRadarSessionForSignup(): Promise<string | undefined> {
  const loader = getStripeLoader();
  if (!loader) {
    return undefined;
  }

  try {
    const stripe = await loader;
    if (!stripe) {
      return undefined;
    }

    const { radarSession, error } = await stripe.createRadarSession();
    if (error || !radarSession) {
      return undefined;
    }

    const sessionId =
      typeof radarSession === 'string' ? radarSession : (radarSession as { id?: string }).id;
    return sessionId && sessionId.length > 0 ? sessionId : undefined;
  } catch {
    return undefined;
  }
}

/** Redact radar session id for client telemetry (same pattern as recaptcha). */
export function redactRadarSessionForLog(radarSession: string | undefined): string | undefined {
  if (!radarSession) {
    return undefined;
  }
  if (radarSession.length <= 8) {
    return 'REDACTED';
  }
  return `${radarSession.slice(0, 8)}...`;
}
